// src/pages/orderAssignment/components/ReassignModal.jsx
import React, { useState } from 'react';
import { X, Users, AlertCircle, RefreshCw, DollarSign, Package, UserPlus } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter/MultiSelectFilter';
import styles from './ReassignModal.module.css';

/**
 * ReassignModal
 *
 * Props:
 *   isOpen            {boolean}
 *   onClose           {function}
 *   onConfirm         {function(statusKeys, assignmentCriteria)} → Promise
 *   selectedUsers     {Array<{userId, userName}>}   — empty when mode='unassigned'
 *   availableStatuses {Array<{key, label, colorCode}>}
 *   users             {Array<{id, name, role}>}
 *   products          {Array<{id, name, sku}>}
 *   isLoading         {boolean}
 *   mode              {'reassign'|'unassigned'}      — default 'reassign'
 */
const ReassignModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedUsers = [],
  availableStatuses = [],
  users = [],
  products = [],
  isLoading = false,
  mode = 'reassign',
}) => {
  const { t } = useLocalization();
  const isUnassignedMode = mode === 'unassigned';

  const [filterByStatus, setFilterByStatus] = useState(false);
  const [selectedStatusKeys, setSelectedStatusKeys] = useState([]);
  const [criteria, setCriteria] = useState({
    grandTotalRules: [],
    productRules: [],
    defaultUserIds: [],
  });
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  // ── Helpers ────────────────────────────────────────────────────────
  const userOptions = users.map((u) => ({
    key: u.id,
    label: `${u.name}${u.role?.name ? ` (${u.role.name})` : ''}`,
  }));

  const productOptions = products.map((p) => ({
    key: p.id,
    label: `${p.name}${p.sku ? ` (${p.sku})` : ''}`,
  }));

  // ── Grand total rule handlers ──────────────────────────────────────
  const addGrandTotalRule = () =>
    setCriteria((prev) => ({
      ...prev,
      grandTotalRules: [...prev.grandTotalRules, { min: 0, max: null, userIds: [] }],
    }));

  const updateGTRule = (idx, field, value) =>
    setCriteria((prev) => ({
      ...prev,
      grandTotalRules: prev.grandTotalRules.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));

  const removeGTRule = (idx) =>
    setCriteria((prev) => ({
      ...prev,
      grandTotalRules: prev.grandTotalRules.filter((_, i) => i !== idx),
    }));

  // ── Product rule handlers ──────────────────────────────────────────
  const addProductRule = () =>
    setCriteria((prev) => ({
      ...prev,
      productRules: [...prev.productRules, { productIds: [], userIds: [] }],
    }));

  const updateProdRule = (idx, field, value) =>
    setCriteria((prev) => ({
      ...prev,
      productRules: prev.productRules.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));

  const removeProdRule = (idx) =>
    setCriteria((prev) => ({
      ...prev,
      productRules: prev.productRules.filter((_, i) => i !== idx),
    }));

  // ── Validation ─────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};

    if (filterByStatus && selectedStatusKeys.length === 0) {
      errs.statuses = 'Please select at least one status';
    }

    criteria.grandTotalRules.forEach((rule, i) => {
      if (rule.userIds.length === 0) errs[`gt_${i}_users`] = t('orderAssignment.atLeastOneUser');
      if (rule.min !== null && rule.max !== null && rule.min >= rule.max)
        errs[`gt_${i}_range`] = t('orderAssignment.maxMustBeGreaterThanMin');
    });

    criteria.productRules.forEach((rule, i) => {
      if (rule.productIds.length === 0) errs[`prod_${i}_products`] = t('orderAssignment.atLeastOneProduct');
      if (rule.userIds.length === 0) errs[`prod_${i}_users`] = t('orderAssignment.atLeastOneUser');
    });

    const hasRules = criteria.grandTotalRules.length > 0 || criteria.productRules.length > 0;
    if (!hasRules && criteria.defaultUserIds.length === 0) {
      errs.general = t('orderAssignment.atLeastOneRuleOrDefault');
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleConfirm = () => {
    if (!validate()) return;
    const statusKeys = filterByStatus && selectedStatusKeys.length > 0 ? selectedStatusKeys : null;
    onConfirm(statusKeys, criteria);
  };

  const handleClose = () => {
    if (isLoading) return;
    // Reset state on close
    setFilterByStatus(false);
    setSelectedStatusKeys([]);
    setCriteria({ grandTotalRules: [], productRules: [], defaultUserIds: [] });
    setErrors({});
    onClose();
  };

  // ── Derived UI strings ─────────────────────────────────────────────
  const headerTitle = isUnassignedMode
    ? t('orderAssignment.assignUnassignedTitle')
    : t('orderAssignment.reassignModalTitle');

  const headerSubtitle = isUnassignedMode
    ? t('orderAssignment.assignUnassignedSubtitle')
    : `${t('orderAssignment.reassignModalSubtitle', { count: selectedUsers.length })}: ${selectedUsers.map((u) => u.userName).join(', ')}`;

  const confirmLabel = isUnassignedMode
    ? t('orderAssignment.executeAssignUnassigned')
    : t('orderAssignment.executeReassign');

  const executingLabel = isUnassignedMode
    ? t('orderAssignment.executingAssignUnassigned')
    : t('orderAssignment.executingReassign');

  const HeaderIcon = isUnassignedMode ? UserPlus : RefreshCw;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`${styles.header} ${isUnassignedMode ? styles.headerUnassigned : ''}`}>
          <div className={styles.headerInfo}>
            <HeaderIcon size={20} className={styles.headerIcon} />
            <div>
              <h2>{headerTitle}</h2>
              <p>{headerSubtitle}</p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={handleClose} disabled={isLoading}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.body}>
          {/* General error */}
          {errors.general && (
            <div className={styles.errorBanner}>
              <AlertCircle size={16} />
              <span>{errors.general}</span>
            </div>
          )}

          {/* Status filter — unified for both modes */}
          <div className={styles.section}>
            <h3>
              {isUnassignedMode
                ? t('orderAssignment.statusFilter')
                : t('orderAssignment.reassignStatusFilter')}
            </h3>

            {isUnassignedMode ? (
              /* Unassigned mode: checkbox-style same as main page */
              <>
                <p className={styles.infoText}>{t('orderAssignment.statusFilterInfo')}</p>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={filterByStatus}
                    onChange={(e) => {
                      setFilterByStatus(e.target.checked);
                      if (!e.target.checked) setSelectedStatusKeys([]);
                    }}
                  />
                  <span>{t('orderAssignment.filterByStatus')}</span>
                </label>

                {filterByStatus && (
                  <div className={styles.statusPickerWrap}>
                    {availableStatuses.length > 0 ? (
                      <div className={styles.statusChips}>
                        {availableStatuses.map((s) => {
                          const active = selectedStatusKeys.includes(s.key);
                          return (
                            <button
                              key={s.key}
                              type="button"
                              className={`${styles.statusChip} ${active ? styles.statusChipActive : ''}`}
                              style={active ? { borderColor: s.colorCode, color: s.colorCode } : {}}
                              onClick={() =>
                                setSelectedStatusKeys((prev) =>
                                  active ? prev.filter((k) => k !== s.key) : [...prev, s.key]
                                )
                              }
                            >
                              <span className={styles.statusDot} style={{ backgroundColor: s.colorCode }} />
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className={styles.infoText} style={{ fontStyle: 'italic' }}>
                        No statuses found for this shop.
                      </p>
                    )}
                    {errors.statuses && (
                      <span className={styles.errorText}>{errors.statuses}</span>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Reassign mode: radio + chips (original) */
              <>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      checked={!filterByStatus}
                      onChange={() => {
                        setFilterByStatus(false);
                        setSelectedStatusKeys([]);
                      }}
                    />
                    {t('orderAssignment.reassignAllStatuses')}
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      checked={filterByStatus}
                      onChange={() => setFilterByStatus(true)}
                    />
                    {t('orderAssignment.reassignSelectedStatuses')}
                  </label>
                </div>

                {filterByStatus && (
                  <div className={styles.statusPickerWrap}>
                    <div className={styles.statusChips}>
                      {availableStatuses.map((s) => {
                        const active = selectedStatusKeys.includes(s.key);
                        return (
                          <button
                            key={s.key}
                            type="button"
                            className={`${styles.statusChip} ${active ? styles.statusChipActive : ''}`}
                            style={active ? { borderColor: s.colorCode, color: s.colorCode } : {}}
                            onClick={() =>
                              setSelectedStatusKeys((prev) =>
                                active ? prev.filter((k) => k !== s.key) : [...prev, s.key]
                              )
                            }
                          >
                            <span className={styles.statusDot} style={{ backgroundColor: s.colorCode }} />
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                    {errors.statuses && (
                      <span className={styles.errorText}>{errors.statuses}</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Criteria */}
          <div className={styles.section}>
            <h3>{t('orderAssignment.reassignCriteria')}</h3>
            <p className={styles.infoText}>{t('orderAssignment.reassignCriteriaInfo')}</p>

            {/* Grand total rules */}
            <div className={styles.ruleBlock}>
              <div className={styles.ruleBlockHeader}>
                <div className={styles.ruleBlockTitle}>
                  <DollarSign size={16} />
                  <span>{t('orderAssignment.grandTotalRules')}</span>
                </div>
                <button className={styles.addBtn} onClick={addGrandTotalRule}>
                  + {t('orderAssignment.addRule')}
                </button>
              </div>

              {criteria.grandTotalRules.map((rule, idx) => (
                <div key={idx} className={styles.ruleCard}>
                  <div className={styles.ruleCardHeader}>
                    <span>{t('orderAssignment.ruleNumber', { number: idx + 1 })}</span>
                    <button className={styles.removeBtn} onClick={() => removeGTRule(idx)}>
                      <X size={14} />
                    </button>
                  </div>
                  <div className={styles.rangeRow}>
                    <div className={styles.formGroup}>
                      <label>{t('orderAssignment.minimumAmount')}</label>
                      <input
                        type="number"
                        className={styles.input}
                        value={rule.min === null ? '' : rule.min}
                        onChange={(e) =>
                          updateGTRule(idx, 'min', e.target.value === '' ? null : parseFloat(e.target.value))
                        }
                        placeholder={t('orderAssignment.noMinimum')}
                      />
                    </div>
                    <span className={styles.rangeSep}>{t('orderAssignment.to')}</span>
                    <div className={styles.formGroup}>
                      <label>{t('orderAssignment.maximumAmount')}</label>
                      <input
                        type="number"
                        className={styles.input}
                        value={rule.max === null ? '' : rule.max}
                        onChange={(e) =>
                          updateGTRule(idx, 'max', e.target.value === '' ? null : parseFloat(e.target.value))
                        }
                        placeholder={t('orderAssignment.noMaximum')}
                      />
                    </div>
                  </div>
                  {errors[`gt_${idx}_range`] && (
                    <span className={styles.errorText}>{errors[`gt_${idx}_range`]}</span>
                  )}
                  <div className={styles.formGroup}>
                    <label>{t('orderAssignment.assignTo')} *</label>
                    <MultiSelectFilter
                      options={userOptions}
                      selectedValues={rule.userIds}
                      onChange={(ids) => updateGTRule(idx, 'userIds', ids)}
                      placeholder={t('orderAssignment.selectUsers')}
                      searchPlaceholder={t('orderAssignment.searchUsers')}
                      displayKey="label"
                      valueKey="key"
                      error={errors[`gt_${idx}_users`]}
                    />
                  </div>
                </div>
              ))}

              {criteria.grandTotalRules.length === 0 && (
                <p className={styles.emptyHint}>{t('orderAssignment.noGrandTotalRules')}</p>
              )}
            </div>

            {/* Product rules */}
            <div className={styles.ruleBlock}>
              <div className={styles.ruleBlockHeader}>
                <div className={styles.ruleBlockTitle}>
                  <Package size={16} />
                  <span>{t('orderAssignment.productRules')}</span>
                </div>
                <button className={styles.addBtn} onClick={addProductRule}>
                  + {t('orderAssignment.addRule')}
                </button>
              </div>

              {criteria.productRules.map((rule, idx) => (
                <div key={idx} className={styles.ruleCard}>
                  <div className={styles.ruleCardHeader}>
                    <span>{t('orderAssignment.ruleNumber', { number: idx + 1 })}</span>
                    <button className={styles.removeBtn} onClick={() => removeProdRule(idx)}>
                      <X size={14} />
                    </button>
                  </div>
                  <div className={styles.formGroup}>
                    <label>{t('orderAssignment.products')} *</label>
                    <MultiSelectFilter
                      options={productOptions}
                      selectedValues={rule.productIds}
                      onChange={(ids) => updateProdRule(idx, 'productIds', ids)}
                      placeholder={t('orderAssignment.selectProducts')}
                      searchPlaceholder={t('orderAssignment.searchProducts')}
                      displayKey="label"
                      valueKey="key"
                      error={errors[`prod_${idx}_products`]}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>{t('orderAssignment.assignTo')} *</label>
                    <MultiSelectFilter
                      options={userOptions}
                      selectedValues={rule.userIds}
                      onChange={(ids) => updateProdRule(idx, 'userIds', ids)}
                      placeholder={t('orderAssignment.selectUsers')}
                      searchPlaceholder={t('orderAssignment.searchUsers')}
                      displayKey="label"
                      valueKey="key"
                      error={errors[`prod_${idx}_users`]}
                    />
                  </div>
                </div>
              ))}

              {criteria.productRules.length === 0 && (
                <p className={styles.emptyHint}>{t('orderAssignment.noProductRules')}</p>
              )}
            </div>

            {/* Default users */}
            <div className={styles.ruleBlock}>
              <div className={styles.ruleBlockHeader}>
                <div className={styles.ruleBlockTitle}>
                  <Users size={16} />
                  <span>{t('orderAssignment.defaultAssignment')}</span>
                </div>
              </div>
              <p className={styles.infoText}>{t('orderAssignment.defaultAssignmentNote')}</p>
              <MultiSelectFilter
                options={userOptions}
                selectedValues={criteria.defaultUserIds}
                onChange={(ids) => setCriteria((prev) => ({ ...prev, defaultUserIds: ids }))}
                placeholder={t('orderAssignment.selectDefaultUsers')}
                searchPlaceholder={t('orderAssignment.searchUsers')}
                displayKey="label"
                valueKey="key"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={handleClose} disabled={isLoading}>
            {t('orderAssignment.cancel')}
          </button>
          <button
            className={`${styles.confirmBtn} ${isUnassignedMode ? styles.confirmBtnUnassigned : ''}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className={styles.spinner} />
                {executingLabel}
              </>
            ) : (
              <>
                <HeaderIcon size={16} />
                {confirmLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReassignModal;